import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usersAPI, authAPI, empresasAPI, equipesAPI, sedesAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { UserPlus, Edit, Trash2, Key, RefreshCw, Building2, Users, Building, Upload, Download, FileSpreadsheet, Loader2, ShieldCheck, Plus, Search, ChevronDown, ChevronUp, MoreVertical, UserCheck, UserX, Crown, Headphones, Camera, ClipboardList } from 'lucide-react';
import UserAvatar from '@/components/UserAvatar';

const Usuarios = () => {
  const [abaAtiva, setAbaAtiva] = useState('usuarios');
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [senhaDialogOpen, setSenhaDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [usuarioSenha, setUsuarioSenha] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    perfil: 'VENDEDOR',
    empresas_permitidas: [],
    equipe_id: '',
    equipe_nome: '',
    sede_id: '',
    sede_nome: '',
    ativo: true,
    // Jornada de Trabalho
    jornada_entrada: '',
    jornada_almoco: '',
    jornada_volta_almoco: '',
    jornada_lanche: '',
    jornada_volta_lanche: '',
    jornada_saida: '',
    ocultar_cronometro: false,
    // Dados Pessoais
    cpf: '',
    rg: '',
    orgao_expedidor: '',
    sexo: '',
    data_nascimento: '',
    telefone1: '',
    telefone2: '',
    telefone3: '',
    // Dados Profissionais
    agente_autorizado: '',
    cidade: '',
    segmento_atuacao: '',
    tipo_atuacao: '',
    produto_atuacao: [],
    classificacao: '',
    uniforme: '',
    tipo_acesso_netsales: '',
    // NetSales
    login_netsales: '',
    codigo_equipe_netsales: '',
    nome_equipe_netsales: '',
  });

  const [novaSenha, setNovaSenha] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importando, setImportando] = useState(false);
  const fileInputRef = useRef(null);

  // Filtros e Paginação
  const [busca, setBusca] = useState('');
  const [filtroPerfil, setFiltroPerfil] = useState('todos');
  const [filtroEmpresa, setFiltroEmpresa] = useState('todas');
  const [filtroStatus, setFiltroStatus] = useState('ativos');
  const [ordenarPor, setOrdenarPor] = useState('nome_asc');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);
  const [menuAberto, setMenuAberto] = useState(null);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [exportando, setExportando] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const fotoInputRef = useRef(null);

  // 2FA
  const [twofaDialogOpen, set2faDialogOpen] = useState(false);
  const [twofaUsuario, set2faUsuario] = useState(null);
  const [twofaQrCode, set2faQrCode] = useState('');
  const [twofaSecret, set2faSecret] = useState('');
  const [twofaCodigo, set2faCodigo] = useState('');

  // Equipe form
  const [equipeDialogOpen, setEquipeDialogOpen] = useState(false);
  const [editandoEquipe, setEditandoEquipe] = useState(null);
  const [equipeForm, setEquipeForm] = useState({
    nome: '',
    descricao: '',
    supervisor_id: '',
    supervisor_nome: '',
    membros_ids: []
  });

  useEffect(() => {
    loadUsuarios();
    loadEmpresas();
    loadEquipes();
    loadSedes();
  }, []);

  const loadUsuarios = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.list();
      setUsuarios(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const loadEmpresas = async () => {
    try {
      const response = await empresasAPI.list();
      setEmpresas(response.data);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  };

  const loadEquipes = async () => {
    try {
      const response = await equipesAPI.listar();
      setEquipes(response.data);
    } catch (error) {
      console.error('Erro ao carregar equipes:', error);
    }
  };

  const loadSedes = async () => {
    try {
      const response = await sedesAPI.listar();
      setSedes(response.data);
    } catch (error) {
      console.error('Erro ao carregar sedes:', error);
    }
  };

  const handleSedeChange = (sedeId) => {
    if (sedeId === 'nenhuma') {
      setFormData({ ...formData, sede_id: '', sede_nome: '' });
    } else {
      const sede = sedes.find(s => s.id === sedeId);
      setFormData({ ...formData, sede_id: sedeId, sede_nome: sede?.nome || '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let userId;
      if (editando) {
        await usersAPI.update(editando.id, formData);
        userId = editando.id;
        toast.success('Usuário atualizado com sucesso!');
      } else {
        const res = await usersAPI.create(formData);
        userId = res.data?.id;
        toast.success('Usuário criado com sucesso!');
      }
      // Upload da foto se selecionada
      if (fotoFile && userId) {
        setUploadingFoto(true);
        try {
          const fd = new FormData();
          fd.append('file', fotoFile);
          await api.post(`/users/${userId}/foto`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Foto enviada com sucesso!');
        } catch (fotoErr) {
          toast.error('Erro ao enviar foto');
          console.error(fotoErr);
        } finally {
          setUploadingFoto(false);
        }
      }
      setDialogOpen(false);
      setEditando(null);
      resetFormData();
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
    } finally {
      setLoading(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      nome: '', email: '', senha: '', perfil: 'VENDEDOR',
      empresas_permitidas: [], equipe_id: '', equipe_nome: '',
      sede_id: '', sede_nome: '', ativo: true,
      jornada_entrada: '', jornada_almoco: '', jornada_volta_almoco: '',
      jornada_lanche: '', jornada_volta_lanche: '', jornada_saida: '', ocultar_cronometro: false,
      cpf: '', rg: '', orgao_expedidor: '', sexo: '', data_nascimento: '',
      telefone1: '', telefone2: '', telefone3: '',
      agente_autorizado: '', cidade: '', segmento_atuacao: '', tipo_atuacao: '',
      produto_atuacao: [], classificacao: '', uniforme: '', tipo_acesso_netsales: '',
      login_netsales: '', codigo_equipe_netsales: '', nome_equipe_netsales: '',
    });
    setFotoFile(null);
    setFotoPreview(null);
  };

  const handleEditar = (usuario) => {
    setEditando(usuario);
    setFotoFile(null);
    setFotoPreview(null);
    setFormData({
      nome: usuario.nome || '',
      email: usuario.email || '',
      senha: '',
      perfil: usuario.perfil || 'VENDEDOR',
      empresas_permitidas: usuario.empresas_permitidas || [],
      equipe_id: usuario.equipe_id || '',
      equipe_nome: usuario.equipe_nome || '',
      sede_id: usuario.sede_id || '',
      sede_nome: usuario.sede_nome || '',
      ativo: usuario.ativo !== false,
      cpf: usuario.cpf || '',
      rg: usuario.rg || '',
      orgao_expedidor: usuario.orgao_expedidor || '',
      sexo: usuario.sexo || '',
      data_nascimento: usuario.data_nascimento || '',
      telefone1: usuario.telefone1 || '',
      telefone2: usuario.telefone2 || '',
      telefone3: usuario.telefone3 || '',
      agente_autorizado: usuario.agente_autorizado || '',
      cidade: usuario.cidade || '',
      segmento_atuacao: usuario.segmento_atuacao || '',
      tipo_atuacao: usuario.tipo_atuacao || '',
      produto_atuacao: Array.isArray(usuario.produto_atuacao) ? usuario.produto_atuacao : usuario.produto_atuacao ? [usuario.produto_atuacao] : [],
      classificacao: usuario.classificacao || '',
      uniforme: usuario.uniforme || '',
      tipo_acesso_netsales: usuario.tipo_acesso_netsales || '',
      login_netsales: usuario.login_netsales || '',
      codigo_equipe_netsales: usuario.codigo_equipe_netsales || '',
      nome_equipe_netsales: usuario.nome_equipe_netsales || '',
      jornada_entrada: usuario.jornada_entrada || '',
      jornada_almoco: usuario.jornada_almoco || '',
      jornada_volta_almoco: usuario.jornada_volta_almoco || '',
      jornada_lanche: usuario.jornada_lanche || '',
      jornada_volta_lanche: usuario.jornada_volta_lanche || '',
      jornada_saida: usuario.jornada_saida || '',
      ocultar_cronometro: usuario.ocultar_cronometro === true,
    });
    setDialogOpen(true);
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este usuário?')) return;
    try {
      await usersAPI.delete(id);
      toast.success('Usuário deletado com sucesso!');
      loadUsuarios();
    } catch (error) {
      toast.error('Erro ao deletar usuário');
    }
  };

  const handleAlterarSenha = async () => {
    if (!novaSenha) { toast.error('Digite a nova senha'); return; }
    try {
      await authAPI.alterarSenha({ user_id: usuarioSenha.id, nova_senha: novaSenha });
      toast.success('Senha alterada com sucesso!');
      setSenhaDialogOpen(false);
      setUsuarioSenha(null);
      setNovaSenha('');
    } catch (error) {
      toast.error('Erro ao alterar senha');
    }
  };

  const handleNovo = () => {
    setEditando(null);
    resetFormData();
    setDialogOpen(true);
  };

  const handleExcluirTodos = async () => {
    if (!window.confirm('ATENÇÃO: Isso vai excluir TODOS os usuários (exceto seu admin). Tem certeza?')) return;
    if (!window.confirm('Confirma a exclusão de todos os usuários?')) return;
    try {
      const response = await usersAPI.excluirTodos();
      toast.success(response.data.message);
      loadUsuarios();
    } catch (error) {
      toast.error('Erro ao excluir usuários');
    }
  };

  const handleBaixarModelo = () => {
    const headers = ['NOME COMPLETO', 'EMAIL', 'PERFIL', 'STATUS'];
    const exemplo = ['JOAO DA SILVA', 'joao@email.com', 'VENDEDOR', 'ATIVO'];
    const csvContent = [headers.join(','), exemplo.join(',')].join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'modelo_importacao_usuarios.csv';
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Modelo baixado!');
  };

  const handleImportarExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Selecione um arquivo .xlsx ou .xls');
      return;
    }
    setImportando(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', file);
      const response = await usersAPI.importar(fd);
      const { criados, erros, senha_padrao } = response.data;
      if (criados > 0) toast.success(`${criados} usuario(s) importado(s)! Senha padrao: ${senha_padrao}`);
      if (erros?.length > 0) erros.forEach(err => toast.error(err));
      if (criados === 0 && (!erros || erros.length === 0)) toast.info('Nenhum usuario encontrado');
      loadUsuarios();
      setImportDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setImportando(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleToggle2FA = async (usuario) => {
    if (usuario.totp_ativo) {
      if (!window.confirm(`Desativar 2FA de ${usuario.nome}?`)) return;
      try {
        await api.post('/auth/2fa/desativar', { user_id: usuario.id });
        toast.success(`2FA desativado para ${usuario.nome}`);
        loadUsuarios();
      } catch (error) {
        toast.error('Erro ao desativar 2FA');
      }
    } else {
      try {
        const res = await api.post('/auth/2fa/ativar', { user_id: usuario.id });
        set2faUsuario(usuario);
        set2faQrCode(res.data.qr_code);
        set2faSecret(res.data.secret);
        set2faCodigo('');
        set2faDialogOpen(true);
      } catch (error) {
        toast.error('Erro ao gerar QR Code');
      }
    }
  };

  const handleConfirmar2FA = async () => {
    if (!twofaCodigo || twofaCodigo.length !== 6) { toast.error('Digite o codigo de 6 digitos'); return; }
    try {
      await api.post('/auth/2fa/confirmar', { user_id: twofaUsuario.id, codigo: twofaCodigo });
      toast.success(`2FA ativado para ${twofaUsuario.nome}!`);
      set2faDialogOpen(false);
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codigo invalido');
    }
  };

  // ===== EQUIPE HANDLERS =====
  const handleNovaEquipe = () => {
    setEditandoEquipe(null);
    setEquipeForm({ nome: '', descricao: '', supervisor_id: '', supervisor_nome: '', membros_ids: [] });
    setEquipeDialogOpen(true);
  };

  const handleEditarEquipe = (equipe) => {
    setEditandoEquipe(equipe);
    setEquipeForm({
      nome: equipe.nome,
      descricao: equipe.descricao || '',
      supervisor_id: equipe.supervisor_id || '',
      supervisor_nome: equipe.supervisor_nome || '',
      membros_ids: equipe.membros_ids || []
    });
    setEquipeDialogOpen(true);
  };

  const handleSalvarEquipe = async () => {
    if (!equipeForm.nome) { toast.error('Nome da equipe é obrigatório'); return; }
    if (!equipeForm.supervisor_id) { toast.error('Selecione um supervisor'); return; }
    try {
      if (editandoEquipe) {
        await equipesAPI.atualizar(editandoEquipe.id, equipeForm);
        toast.success('Equipe atualizada!');
      } else {
        await equipesAPI.criar(equipeForm);
        toast.success('Equipe criada!');
      }
      setEquipeDialogOpen(false);
      loadEquipes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar equipe');
    }
  };

  const handleExcluirEquipe = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta equipe?')) return;
    try {
      await equipesAPI.excluir(id);
      toast.success('Equipe excluída!');
      loadEquipes();
    } catch (error) {
      toast.error('Erro ao excluir equipe');
    }
  };

  const supervisores = usuarios.filter(u => u.perfil === 'SUPERVISOR');
  const vendedores = usuarios.filter(u => u.perfil === 'VENDEDOR');

  const getEquipesDoUsuario = (userId) => {
    return equipes.filter(eq => eq.membros_ids?.includes(userId));
  };

  // Stats
  const stats = useMemo(() => ({
    total: usuarios.length,
    admins: usuarios.filter(u => u.perfil === 'ADMIN').length,
    vendedoresCount: usuarios.filter(u => u.perfil === 'VENDEDOR' && u.ativo !== false).length,
    bko: usuarios.filter(u => u.perfil === 'BKO').length,
    bkoMailing: usuarios.filter(u => u.perfil === 'BKO_MAILING').length,
    inativos: usuarios.filter(u => u.ativo === false).length,
  }), [usuarios]);

  // Filtered & sorted users
  const usuariosFiltrados = useMemo(() => {
    let filtered = [...usuarios];
    if (busca) {
      const q = busca.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      filtered = filtered.filter(u => {
        const nome = (u.nome || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const email = (u.email || '').toLowerCase();
        const cpf = (u.cpf || '').toLowerCase();
        return nome.includes(q) || email.includes(q) || cpf.includes(q);
      });
    }
    if (filtroPerfil !== 'todos') {
      filtered = filtered.filter(u => u.perfil === filtroPerfil);
    }
    if (filtroEmpresa !== 'todas') {
      filtered = filtered.filter(u => u.empresas_permitidas?.includes(filtroEmpresa));
    }
    if (filtroStatus === 'ativos') {
      filtered = filtered.filter(u => u.ativo !== false);
    } else if (filtroStatus === 'inativos') {
      filtered = filtered.filter(u => u.ativo === false);
    }
    // Sort
    filtered.sort((a, b) => {
      if (ordenarPor === 'nome_asc') return (a.nome || '').localeCompare(b.nome || '');
      if (ordenarPor === 'nome_desc') return (b.nome || '').localeCompare(a.nome || '');
      if (ordenarPor === 'recente') return (b.criado_em || '').localeCompare(a.criado_em || '');
      return 0;
    });
    return filtered;
  }, [usuarios, busca, filtroPerfil, filtroEmpresa, filtroStatus, ordenarPor]);

  const totalPaginas = Math.ceil(usuariosFiltrados.length / itensPorPagina);
  const usuariosPaginados = usuariosFiltrados.slice((paginaAtual - 1) * itensPorPagina, paginaAtual * itensPorPagina);

  // Reset page when filters change
  useEffect(() => { setPaginaAtual(1); }, [busca, filtroPerfil, filtroEmpresa]);

  // Close dropdown menu when clicking outside
  useEffect(() => {
    if (!menuAberto) return;
    const handler = () => setMenuAberto(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuAberto]);

  const getEmpresaNome = (userId) => {
    const user = usuarios.find(u => u.id === userId);
    if (user?.empresas_permitidas?.length > 0) {
      const emp = empresas.find(e => e.id === user.empresas_permitidas[0]);
      return emp ? { nome: emp.nome, cnpj: emp.cnpj || '' } : null;
    }
    if (user?.agente_autorizado) {
      return { nome: user.agente_autorizado, cnpj: '' };
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciar Usuarios</h1>
          <p className="text-gray-500 mt-1">Administre os usuarios e equipes do sistema</p>
        </div>
      </div>

      {/* Abas */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setAbaAtiva('usuarios')}
          className={`px-4 py-2.5 font-medium text-sm transition-all border-b-2 -mb-[2px] ${
            abaAtiva === 'usuarios' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="aba-usuarios"
        >
          <Users className="h-4 w-4 inline mr-2" />
          Usuarios ({usuarios.length})
        </button>
        <button
          onClick={() => setAbaAtiva('equipes')}
          className={`px-4 py-2.5 font-medium text-sm transition-all border-b-2 -mb-[2px] ${
            abaAtiva === 'equipes' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="aba-equipes"
        >
          <Building2 className="h-4 w-4 inline mr-2" />
          Equipes de Vendas ({equipes.length})
        </button>
      </div>

      {/* ============ TAB USUARIOS ============ */}
      {abaAtiva === 'usuarios' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="usuarios-stats">
            <div className="bg-white dark:bg-gray-800 rounded-xl border p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Users className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-gray-500">Total de Usuarios</p>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700/30 flex items-center justify-center">
                <Crown className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.admins}</p>
                <p className="text-xs text-gray-500">Administradores</p>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.vendedoresCount}</p>
                <p className="text-xs text-gray-500">Vendedores</p>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Headphones className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.bko}</p>
                <p className="text-xs text-gray-500">Back Office</p>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <UserX className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.inativos}</p>
                <p className="text-xs text-gray-500">Inativos</p>
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-gray-800 rounded-xl border p-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="usuarios-busca"
                placeholder="Buscar por nome ou e-mail..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filtroPerfil} onValueChange={setFiltroPerfil}>
              <SelectTrigger className="w-[160px]" data-testid="filtro-perfil">
                <SelectValue placeholder="Todos os perfis" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os perfis</SelectItem>
                <SelectItem value="ADMIN">Admin</SelectItem>
                <SelectItem value="GERENTE">Gerente</SelectItem>
                <SelectItem value="SUPERVISOR">Supervisor</SelectItem>
                <SelectItem value="BKO">BKO</SelectItem>
                <SelectItem value="BKO_MAILING">BKO Mailing</SelectItem>
                <SelectItem value="VENDEDOR">Vendedor</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filtroEmpresa} onValueChange={setFiltroEmpresa}>
              <SelectTrigger className="w-[180px]" data-testid="filtro-empresa">
                <SelectValue placeholder="Todas as empresas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as empresas</SelectItem>
                {empresas.filter(e => e.ativo).map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>{emp.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-[140px]" data-testid="filtro-status-usuario">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="ativos">Ativos</SelectItem>
                <SelectItem value="inativos">Inativos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={ordenarPor} onValueChange={setOrdenarPor}>
              <SelectTrigger className="w-[150px]" data-testid="ordenar-por">
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nome_asc">Nome A-Z</SelectItem>
                <SelectItem value="nome_desc">Nome Z-A</SelectItem>
                <SelectItem value="recente">Mais recente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleExcluirTodos} variant="outline" className="text-red-600 hover:bg-red-50" data-testid="excluir-todos-usuarios-btn">
              <Trash2 className="mr-2 h-4 w-4" />Excluir Todos
            </Button>
            <Button onClick={() => setImportDialogOpen(true)} variant="outline" className="text-gray-700 border-gray-300 hover:bg-gray-50" data-testid="importar-usuarios-btn">
              <Upload className="mr-2 h-4 w-4" />Importar Excel
            </Button>
            <Button
              onClick={async () => {
                setExportando(true);
                try {
                  const response = await usersAPI.exportar();
                  const url = window.URL.createObjectURL(new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  }));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `usuarios_${Date.now()}.xlsx`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Planilha de usuários exportada com sucesso!');
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Erro ao exportar usuários');
                } finally {
                  setExportando(false);
                }
              }}
              variant="outline"
              className="text-green-700 border-green-300 hover:bg-green-50"
              disabled={exportando}
              data-testid="exportar-usuarios-btn"
            >
              {exportando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
              Exportar Excel
            </Button>
            <Button onClick={loadUsuarios} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />Atualizar
            </Button>
            <Button onClick={handleNovo} className="bg-red-600 hover:bg-red-700 ml-auto" data-testid="novo-usuario-btn">
              <UserPlus className="mr-2 h-4 w-4" />Novo Usuario
            </Button>
          </div>

          {/* Users Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Perfil</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Equipe / Sede</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Situacao</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[100px]">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {usuariosPaginados.map((usuario) => {
                  const equipesDo = getEquipesDoUsuario(usuario.id);
                  const isExpanded = expandedRow === usuario.id;
                  const empInfo = getEmpresaNome(usuario.id);
                  return (
                    <React.Fragment key={usuario.id}>
                      <tr 
                        className={`border-b hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer ${isExpanded ? 'bg-gray-50 dark:bg-gray-700/20' : ''}`}
                        onClick={() => setExpandedRow(isExpanded ? null : usuario.id)}
                        data-testid={`user-row-${usuario.id}`}
                      >
                        {/* Nome + Email + CPF */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <UserAvatar fotoPath={usuario.foto_path} nome={usuario.nome} size="md" />
                            <div>
                              <p className="font-semibold text-sm">{usuario.nome}</p>
                              <p className="text-xs text-gray-500">{usuario.email}</p>
                              {usuario.cpf && <p className="text-xs text-gray-400">{usuario.cpf}</p>}
                            </div>
                          </div>
                        </td>
                        {/* Perfil */}
                        <td className="px-4 py-3 hidden md:table-cell">
                          <Badge className={
                            usuario.perfil === 'ADMIN' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                            usuario.perfil === 'GERENTE' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                            usuario.perfil === 'SUPERVISOR' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                            usuario.perfil === 'BKO' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300' :
                            usuario.perfil === 'BKO_MAILING' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                            'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          }>
                            {usuario.perfil}
                          </Badge>
                        </td>
                        {/* Equipe / Sede */}
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {equipesDo.length > 0 && (
                            <p className="text-sm font-medium">{equipesDo.map(e => e.nome).join(', ')}</p>
                          )}
                          {usuario.sede_nome && (
                            <p className="text-xs text-gray-500">{usuario.sede_nome}</p>
                          )}
                          {!equipesDo.length && !usuario.sede_nome && (
                            <span className="text-xs text-gray-400 italic">-</span>
                          )}
                        </td>
                        {/* Situação */}
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${
                            usuario.ativo !== false
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-400 text-white'
                          }`}>
                            {usuario.ativo !== false ? 'AT' : 'IN'}
                          </span>
                        </td>
                        {/* Ações */}
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            <div className="relative">
                              <Button 
                                size="sm" variant="ghost" className="h-8 w-8 p-0"
                                onClick={(e) => { e.stopPropagation(); setMenuAberto(menuAberto === usuario.id ? null : usuario.id); }}
                                data-testid={`user-menu-${usuario.id}`}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                              {menuAberto === usuario.id && (
                                <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border rounded-lg shadow-lg z-50 py-1 min-w-[160px]">
                                  <button className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                    onClick={() => { handleEditar(usuario); setMenuAberto(null); }}>
                                    <Edit className="h-3.5 w-3.5" /> Editar
                                  </button>
                                  <button className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                    onClick={() => { setUsuarioSenha(usuario); setSenhaDialogOpen(true); setMenuAberto(null); }}>
                                    <Key className="h-3.5 w-3.5" /> Alterar Senha
                                  </button>
                                  <hr className="my-1" />
                                  <button className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 flex items-center gap-2"
                                    onClick={() => { handleDeletar(usuario.id); setMenuAberto(null); }}>
                                    <Trash2 className="h-3.5 w-3.5" /> Deletar
                                  </button>
                                </div>
                              )}
                            </div>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                              onClick={(e) => { e.stopPropagation(); setExpandedRow(isExpanded ? null : usuario.id); }}>
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {/* Expanded Details */}
                      {isExpanded && (
                        <tr className="bg-gray-50 dark:bg-gray-700/20 border-b" data-testid={`user-expanded-${usuario.id}`}>
                          <td colSpan={5} className="px-4 py-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Equipe de Vendas</p>
                                <p className="text-sm font-semibold">{equipesDo.length > 0 ? equipesDo.map(e => e.nome).join(', ') : '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Classificacao</p>
                                <p className="text-sm font-semibold">{usuario.classificacao || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Segmento de Atuacao</p>
                                <p className="text-sm font-semibold">{usuario.segmento_atuacao || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Tipo de Atuacao</p>
                                <p className="text-sm font-semibold">{usuario.tipo_atuacao || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Sede</p>
                                <p className="text-sm font-semibold">{usuario.sede_nome || '-'}</p>
                              </div>
                              {usuario.cidade && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Cidade</p>
                                  <p className="text-sm font-semibold">{usuario.cidade}</p>
                                </div>
                              )}
                              {usuario.telefone1 && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Telefone</p>
                                  <p className="text-sm font-semibold">{usuario.telefone1}</p>
                                </div>
                              )}
                              {usuario.agente_autorizado && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Agente Autorizado</p>
                                  <p className="text-sm font-semibold">{usuario.agente_autorizado}</p>
                                </div>
                              )}
                              {(usuario.perfil === 'BKO' || usuario.perfil === 'BKO_MAILING') && usuario.empresas_permitidas?.length > 0 && (
                                <div className="col-span-2">
                                  <p className="text-xs text-gray-500 mb-1">Empresas Permitidas</p>
                                  <div className="flex flex-wrap gap-1">
                                    {usuario.empresas_permitidas.map(empId => {
                                      const empresa = empresas.find(e => e.id === empId);
                                      return empresa ? (
                                        <Badge key={empId} variant="outline" className="text-xs">{empresa.nome}</Badge>
                                      ) : null;
                                    })}
                                  </div>
                                </div>
                              )}
                              {(usuario.jornada_entrada || usuario.jornada_saida) && (
                                <div className="col-span-2 mt-2 border-t pt-2 border-gray-200 dark:border-gray-700">
                                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1"><ClipboardList className="h-3 w-3" /> Jornada de Trabalho</p>
                                  <div className="grid grid-cols-6 gap-2">
                                    <div className="text-center">
                                      <p className="text-[10px] text-gray-400">Entrada</p>
                                      <p className="text-sm font-semibold text-green-600">{usuario.jornada_entrada || '-'}</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-[10px] text-gray-400">Almoço</p>
                                      <p className="text-sm font-semibold text-orange-600">{usuario.jornada_almoco || '-'}</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-[10px] text-gray-400">Volta Almoço</p>
                                      <p className="text-sm font-semibold text-blue-600">{usuario.jornada_volta_almoco || '-'}</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-[10px] text-gray-400">Lanche</p>
                                      <p className="text-sm font-semibold text-orange-600">{usuario.jornada_lanche || '-'}</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-[10px] text-gray-400">Volta Lanche</p>
                                      <p className="text-sm font-semibold text-blue-600">{usuario.jornada_volta_lanche || '-'}</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-[10px] text-gray-400">Saída</p>
                                      <p className="text-sm font-semibold text-red-600">{usuario.jornada_saida || '-'}</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {usuariosPaginados.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                      {busca || filtroPerfil !== 'todos' || filtroEmpresa !== 'todas'
                        ? 'Nenhum usuario encontrado com os filtros aplicados'
                        : 'Nenhum usuario cadastrado'
                      }
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {usuariosFiltrados.length > 0 && (
              <div className="flex flex-wrap items-center justify-between px-4 py-3 border-t bg-gray-50 dark:bg-gray-900/30 gap-2">
                <p className="text-xs text-gray-500">
                  Mostrando {((paginaAtual - 1) * itensPorPagina) + 1} a {Math.min(paginaAtual * itensPorPagina, usuariosFiltrados.length)} de {usuariosFiltrados.length} usuarios
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" disabled={paginaAtual === 1}
                    onClick={() => setPaginaAtual(p => p - 1)} className="h-8 px-2 text-xs">
                    &laquo;
                  </Button>
                  {Array.from({ length: Math.min(totalPaginas, 7) }, (_, i) => {
                    let pageNum;
                    if (totalPaginas <= 7) {
                      pageNum = i + 1;
                    } else if (paginaAtual <= 4) {
                      pageNum = i + 1;
                    } else if (paginaAtual >= totalPaginas - 3) {
                      pageNum = totalPaginas - 6 + i;
                    } else {
                      pageNum = paginaAtual - 3 + i;
                    }
                    return (
                      <Button key={pageNum} variant={paginaAtual === pageNum ? 'default' : 'outline'}
                        size="sm" onClick={() => setPaginaAtual(pageNum)}
                        className={`h-8 w-8 p-0 text-xs ${paginaAtual === pageNum ? 'bg-red-600 hover:bg-red-700' : ''}`}>
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button variant="outline" size="sm" disabled={paginaAtual === totalPaginas}
                    onClick={() => setPaginaAtual(p => p + 1)} className="h-8 px-2 text-xs">
                    &raquo;
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Itens por pagina:</span>
                  <Select value={String(itensPorPagina)} onValueChange={(v) => { setItensPorPagina(Number(v)); setPaginaAtual(1); }}>
                    <SelectTrigger className="w-[65px] h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ============ TAB EQUIPES ============ */}
      {abaAtiva === 'equipes' && (
        <>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={loadEquipes} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />Atualizar
            </Button>
            <Button onClick={handleNovaEquipe} className="bg-purple-600 hover:bg-purple-700" data-testid="nova-equipe-btn">
              <Plus className="mr-2 h-4 w-4" />Nova Equipe
            </Button>
          </div>

          {equipes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500 font-medium">Nenhuma equipe criada</p>
                <p className="text-sm text-gray-400 mt-1">Crie equipes para organizar vendedores por supervisor</p>
                <Button onClick={handleNovaEquipe} className="mt-4 bg-purple-600 hover:bg-purple-700">
                  <Plus className="mr-2 h-4 w-4" />Criar Primeira Equipe
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {equipes.map(equipe => (
                <Card key={equipe.id} className="border-l-4 border-l-purple-500">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5 text-purple-600" />
                        {equipe.nome}
                      </CardTitle>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEditarEquipe(equipe)} data-testid={`editar-equipe-${equipe.id}`}>
                          <Edit className="h-4 w-4 text-gray-500" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleExcluirEquipe(equipe.id)} data-testid={`excluir-equipe-${equipe.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    {equipe.descricao && <p className="text-sm text-gray-500">{equipe.descricao}</p>}
                  </CardHeader>
                  <CardContent>
                    {/* Supervisor */}
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-400 uppercase mb-1">Supervisor</p>
                      {equipe.supervisor_nome ? (
                        <Badge className="bg-yellow-100 text-yellow-800">
                          <ShieldCheck className="h-3 w-3 mr-1" />{equipe.supervisor_nome}
                        </Badge>
                      ) : (
                        <span className="text-sm text-red-500">Nenhum supervisor definido</span>
                      )}
                    </div>
                    {/* Membros */}
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase mb-1">Membros ({equipe.membros?.length || 0})</p>
                      {equipe.membros?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {equipe.membros.map(m => (
                            <Badge key={m.id} variant="outline" className="text-xs">
                              {m.nome}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Nenhum membro</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* ===== DIALOGS ===== */}
      {/* Dialog Criar/Editar Usuario */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Seção: Foto de Perfil */}
            <div className="flex items-center gap-4 pb-3 border-b">
              <div className="relative group">
                {fotoPreview ? (
                  <img src={fotoPreview} alt="Preview" className="w-16 h-[85px] rounded-md object-cover border-2 border-gray-200" />
                ) : editando?.foto_path ? (
                  <UserAvatar fotoPath={editando.foto_path} nome={editando.nome} size="xl" />
                ) : (
                  <div className="w-16 h-[85px] rounded-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <Camera className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fotoInputRef.current?.click()}
                  className="absolute inset-0 rounded-md bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <Camera className="h-5 w-5 text-white" />
                </button>
                <input
                  ref={fotoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) { toast.error('Imagem muito grande. Máximo 5MB.'); return; }
                    setFotoFile(file);
                    setFotoPreview(URL.createObjectURL(file));
                  }}
                  data-testid="user-foto-input"
                />
              </div>
              <div>
                <p className="text-sm font-medium">Foto de Perfil</p>
                <p className="text-xs text-gray-500">JPG, PNG, WebP ou GIF. Maximo 5MB.</p>
                <Button type="button" variant="outline" size="sm" className="mt-1 h-7 text-xs"
                  onClick={() => fotoInputRef.current?.click()}>
                  <Camera className="mr-1 h-3 w-3" />{fotoPreview || editando?.foto_path ? 'Trocar Foto' : 'Adicionar Foto'}
                </Button>
              </div>
            </div>

            {/* Seção: Informações Pessoais */}
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 border-b pb-1">Informações Pessoais</h3>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-xs">Nome Completo *</Label>
                  <Input data-testid="user-nome" value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value.toUpperCase() })} required className="uppercase" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">CPF</Label>
                  <Input data-testid="user-cpf" placeholder="000.000.000-00" value={formData.cpf} onChange={(e) => setFormData({ ...formData, cpf: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">RG</Label>
                  <Input data-testid="user-rg" value={formData.rg} onChange={(e) => setFormData({ ...formData, rg: e.target.value.toUpperCase() })} className="uppercase" />
                </div>
                <div>
                  <Label className="text-xs">Orgão Expedidor</Label>
                  <Input data-testid="user-orgao" placeholder="SSP/ES" value={formData.orgao_expedidor} onChange={(e) => setFormData({ ...formData, orgao_expedidor: e.target.value.toUpperCase() })} className="uppercase" />
                </div>
              </div>
              <div>
                <Label className="text-xs">E-mail (Gmail, Hotmail, Yahoo) *</Label>
                <Input data-testid="user-email" type="email" placeholder="seuemail@gmail.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value.toLowerCase() })} required />
                <p className="text-[10px] text-gray-400 mt-0.5">Usado para login e recuperacao de senha</p>
              </div>
              {!editando && (
                <div>
                  <Label className="text-xs">Senha *</Label>
                  <Input data-testid="user-senha" type="password" value={formData.senha} onChange={(e) => setFormData({ ...formData, senha: e.target.value })} required />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Sexo</Label>
                  <Select value={formData.sexo || 'nenhum'} onValueChange={(v) => setFormData({ ...formData, sexo: v === 'nenhum' ? '' : v })}>
                    <SelectTrigger data-testid="user-sexo"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhum">Selecione...</SelectItem>
                      <SelectItem value="Masculino">Masculino</SelectItem>
                      <SelectItem value="Feminino">Feminino</SelectItem>
                      <SelectItem value="Outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Data de Nascimento</Label>
                  <Input data-testid="user-nascimento" type="date" value={formData.data_nascimento} onChange={(e) => setFormData({ ...formData, data_nascimento: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Telefone 01</Label>
                  <Input data-testid="user-tel1" placeholder="(00) 00000-0000" value={formData.telefone1} onChange={(e) => setFormData({ ...formData, telefone1: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Telefone 02</Label>
                  <Input data-testid="user-tel2" placeholder="(00) 00000-0000" value={formData.telefone2} onChange={(e) => setFormData({ ...formData, telefone2: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Telefone 03</Label>
                  <Input data-testid="user-tel3" placeholder="(00) 00000-0000" value={formData.telefone3} onChange={(e) => setFormData({ ...formData, telefone3: e.target.value })} />
                </div>
              </div>
            </div>

            {/* Seção: Informações Profissionais */}
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 border-b pb-1">Informações Profissionais</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Perfil *</Label>
                  <Select value={formData.perfil} onValueChange={(value) => setFormData({ ...formData, perfil: value })}>
                    <SelectTrigger data-testid="user-perfil"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ADMIN">Admin</SelectItem>
                      <SelectItem value="GERENTE">Gerente</SelectItem>
                      <SelectItem value="SUPERVISOR">Supervisor</SelectItem>
                      <SelectItem value="BKO">BKO</SelectItem>
                      <SelectItem value="BKO_MAILING">BKO Mailing</SelectItem>
                      <SelectItem value="VENDEDOR">Vendedor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={formData.ativo.toString()} onValueChange={(value) => setFormData({ ...formData, ativo: value === 'true' })}>
                    <SelectTrigger data-testid="user-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Ativo</SelectItem>
                      <SelectItem value="false">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Sede *</Label>
                <Select value={formData.sede_id || 'nenhuma'} onValueChange={handleSedeChange}>
                  <SelectTrigger data-testid="user-sede"><SelectValue placeholder="Selecione a sede..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nenhuma">Selecione a sede...</SelectItem>
                    {sedes.filter(s => s.ativo).map(sede => (
                      <SelectItem key={sede.id} value={sede.id}>{sede.nome} ({sede.codigo})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Equipe de Vendas</Label>
                <Select value={formData.equipe_id || 'nenhuma'} onValueChange={(v) => {
                  if (v === 'nenhuma') {
                    setFormData({ ...formData, equipe_id: '', equipe_nome: '' });
                  } else {
                    const eq = equipes.find(e => e.id === v);
                    setFormData({ ...formData, equipe_id: v, equipe_nome: eq?.nome || '' });
                  }
                }}>
                  <SelectTrigger data-testid="user-equipe"><SelectValue placeholder="Selecione a equipe..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nenhuma">Sem equipe</SelectItem>
                    {equipes.map(eq => (
                      <SelectItem key={eq.id} value={eq.id}>{eq.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Cidade</Label>
                  <Input data-testid="user-cidade" value={formData.cidade} onChange={(e) => setFormData({ ...formData, cidade: e.target.value.toUpperCase() })} placeholder="Cidade" className="uppercase" />
                </div>
                <div>
                  <Label className="text-xs">Segmento de Atuação</Label>
                  <Select value={formData.segmento_atuacao || 'nenhum'} onValueChange={(v) => setFormData({ ...formData, segmento_atuacao: v === 'nenhum' ? '' : v })}>
                    <SelectTrigger data-testid="user-segmento"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhum">Selecione...</SelectItem>
                      <SelectItem value="Residencial">Residencial</SelectItem>
                      <SelectItem value="Empresarial">Empresarial</SelectItem>
                      <SelectItem value="Corporativo">Corporativo</SelectItem>
                      <SelectItem value="Governo">Governo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tipo de Atuação</Label>
                  <Select value={formData.tipo_atuacao || 'nenhum'} onValueChange={(v) => setFormData({ ...formData, tipo_atuacao: v === 'nenhum' ? '' : v })}>
                    <SelectTrigger data-testid="user-tipo-atuacao"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhum">Selecione...</SelectItem>
                      <SelectItem value="Presencial">Presencial</SelectItem>
                      <SelectItem value="Home Office">Home Office</SelectItem>
                      <SelectItem value="Porta a Porta">Porta a Porta</SelectItem>
                      <SelectItem value="Loja">Loja</SelectItem>
                      <SelectItem value="Televendas">Televendas</SelectItem>
                      <SelectItem value="Digital">Digital</SelectItem>
                      <SelectItem value="Externo">Externo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <Label className="text-xs mb-2 block">Produto de Atuação</Label>
                  <div className="flex gap-3 flex-wrap">
                    {['Claro', 'Vivo'].map(prod => (
                      <div key={prod} className="flex items-center space-x-2">
                        <Checkbox
                          id={`prod-${prod}`}
                          data-testid={`user-produto-${prod.toLowerCase()}`}
                          checked={
                            Array.isArray(formData.produto_atuacao)
                              ? formData.produto_atuacao.includes(prod)
                              : formData.produto_atuacao === prod || formData.produto_atuacao === 'Ambas'
                          }
                          onCheckedChange={(checked) => {
                            let current = Array.isArray(formData.produto_atuacao)
                              ? [...formData.produto_atuacao]
                              : formData.produto_atuacao === 'Ambas' ? ['Claro', 'Vivo']
                              : formData.produto_atuacao ? [formData.produto_atuacao]
                              : [];
                            if (checked) {
                              if (!current.includes(prod)) current.push(prod);
                            } else {
                              current = current.filter(p => p !== prod);
                            }
                            setFormData({ ...formData, produto_atuacao: current });
                          }}
                        />
                        <label htmlFor={`prod-${prod}`} className="text-sm font-medium cursor-pointer">{prod}</label>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Classificação</Label>
                  <Select value={formData.classificacao || 'nenhum'} onValueChange={(v) => setFormData({ ...formData, classificacao: v === 'nenhum' ? '' : v })}>
                    <SelectTrigger data-testid="user-classificacao"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhum">Selecione...</SelectItem>
                      <SelectItem value="Junior">Junior</SelectItem>
                      <SelectItem value="Pleno">Pleno</SelectItem>
                      <SelectItem value="Senior">Senior</SelectItem>
                      <SelectItem value="Especialista">Especialista</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Uniforme</Label>
                  <Select value={formData.uniforme || 'nenhum'} onValueChange={(v) => setFormData({ ...formData, uniforme: v === 'nenhum' ? '' : v })}>
                    <SelectTrigger data-testid="user-uniforme"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhum">Selecione...</SelectItem>
                      <SelectItem value="Sim">Sim</SelectItem>
                      <SelectItem value="Não">Não</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Tipo de Acesso do Net Sales</Label>
                <Select value={formData.tipo_acesso_netsales || 'nenhum'} onValueChange={(v) => setFormData({ ...formData, tipo_acesso_netsales: v === 'nenhum' ? '' : v })}>
                  <SelectTrigger data-testid="user-acesso-netsales"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nenhum">Selecione...</SelectItem>
                    <SelectItem value="Administrador">Administrador</SelectItem>
                    <SelectItem value="Supervisor">Supervisor</SelectItem>
                    <SelectItem value="Vendedor">Vendedor</SelectItem>
                    <SelectItem value="Consulta">Consulta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Jornada de Trabalho */}
            <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50 dark:border-gray-700">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardList className="h-5 w-5 text-red-600" />
                <Label className="text-gray-800 dark:text-gray-200 font-semibold">Jornada de Trabalho</Label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Entrada</Label>
                  <Input type="time" data-testid="jornada-entrada" value={formData.jornada_entrada} onChange={(e) => setFormData({ ...formData, jornada_entrada: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Almoço</Label>
                  <Input type="time" data-testid="jornada-almoco" value={formData.jornada_almoco} onChange={(e) => setFormData({ ...formData, jornada_almoco: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Volta do Almoço</Label>
                  <Input type="time" data-testid="jornada-volta-almoco" value={formData.jornada_volta_almoco} onChange={(e) => setFormData({ ...formData, jornada_volta_almoco: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Lanche</Label>
                  <Input type="time" data-testid="jornada-lanche" value={formData.jornada_lanche} onChange={(e) => setFormData({ ...formData, jornada_lanche: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Volta do Lanche</Label>
                  <Input type="time" data-testid="jornada-volta-lanche" value={formData.jornada_volta_lanche} onChange={(e) => setFormData({ ...formData, jornada_volta_lanche: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Saída</Label>
                  <Input type="time" data-testid="jornada-saida" value={formData.jornada_saida} onChange={(e) => setFormData({ ...formData, jornada_saida: e.target.value })} />
                </div>
              </div>
              <label className="mt-3 flex items-start gap-2 cursor-pointer rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-2.5" data-testid="ocultar-cronometro-label">
                <Checkbox
                  checked={formData.ocultar_cronometro === true}
                  onCheckedChange={(v) => setFormData({ ...formData, ocultar_cronometro: v === true })}
                  data-testid="ocultar-cronometro-checkbox"
                  className="mt-0.5"
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">
                  <span className="font-semibold">Ocultar cronômetro da jornada</span> — este usuário vê no topo apenas o status (Trabalhando / Almoço), sem o tempo trabalhado. As horas continuam sendo registradas normalmente no Controle de Jornada.
                </span>
              </label>
            </div>

            {/* Empresas Permitidas - Para BKO e BKO_MAILING */}
            {(formData.perfil === 'BKO' || formData.perfil === 'BKO_MAILING') && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-5 w-5 text-red-600" />
                  <Label className="text-gray-800 font-semibold">Empresas Permitidas para este BKO</Label>
                </div>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {empresas.filter(e => e.ativo).map((empresa) => (
                    <div key={empresa.id} className="flex items-center space-x-2 p-2 bg-white rounded border">
                      <Checkbox
                        id={`empresa-${empresa.id}`}
                        checked={formData.empresas_permitidas.includes(empresa.id)}
                        onCheckedChange={(checked) => {
                          setFormData({
                            ...formData,
                            empresas_permitidas: checked
                              ? [...formData.empresas_permitidas, empresa.id]
                              : formData.empresas_permitidas.filter(id => id !== empresa.id)
                          });
                        }}
                      />
                      <label htmlFor={`empresa-${empresa.id}`} className="text-sm font-medium cursor-pointer">{empresa.nome}</label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={loading}>{loading ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Alterar Senha */}
      <Dialog open={senhaDialogOpen} onOpenChange={setSenhaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar Senha - {usuarioSenha?.nome}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nova Senha *</Label>
              <Input type="password" value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} placeholder="Digite a nova senha" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSenhaDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleAlterarSenha}>Alterar Senha</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Importar Excel */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-red-600" />Importar Usuarios via Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-800 font-medium mb-2">Formato da planilha (colunas):</p>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <span className="bg-gray-100 px-2 py-1 rounded text-center font-semibold">NOME COMPLETO</span>
                <span className="bg-gray-100 px-2 py-1 rounded text-center font-semibold">EMAIL</span>
                <span className="bg-gray-100 px-2 py-1 rounded text-center font-semibold">PERFIL</span>
                <span className="bg-gray-100 px-2 py-1 rounded text-center font-semibold">STATUS</span>
              </div>
              <p className="text-xs text-amber-700 mt-2 font-medium">Senha padrao para todos importados: 123456</p>
            </div>
            <Button variant="outline" onClick={handleBaixarModelo} className="w-full" data-testid="baixar-modelo-btn">
              <Download className="mr-2 h-4 w-4" />Baixar Modelo de Planilha
            </Button>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImportarExcel} className="hidden" data-testid="input-importar-arquivo" />
              {importando ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 text-red-600 animate-spin" />
                  <p className="text-sm text-gray-600">Importando usuarios...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-gray-400" />
                  <Button onClick={() => fileInputRef.current?.click()} className="bg-red-600 hover:bg-red-700" data-testid="selecionar-arquivo-btn">
                    Selecionar Arquivo Excel
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Criar/Editar Equipe */}
      <Dialog open={equipeDialogOpen} onOpenChange={setEquipeDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              {editandoEquipe ? 'Editar Equipe' : 'Nova Equipe'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome da Equipe *</Label>
                <Input value={equipeForm.nome} onChange={(e) => setEquipeForm({...equipeForm, nome: e.target.value})}
                  placeholder="Ex: Equipe Alfa" data-testid="equipe-nome-input" />
              </div>
              <div>
                <Label>Descricao</Label>
                <Input value={equipeForm.descricao} onChange={(e) => setEquipeForm({...equipeForm, descricao: e.target.value})}
                  placeholder="Descricao da equipe" />
              </div>
            </div>

            {/* Supervisor */}
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <Label className="text-yellow-800 font-semibold flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />Supervisor Responsavel *
              </Label>
              <Select value={equipeForm.supervisor_id || 'nenhum'}
                onValueChange={(val) => {
                  if (val === 'nenhum') {
                    setEquipeForm({...equipeForm, supervisor_id: '', supervisor_nome: ''});
                  } else {
                    const sup = supervisores.find(s => s.id === val);
                    setEquipeForm({...equipeForm, supervisor_id: val, supervisor_nome: sup?.nome || ''});
                  }
                }}>
                <SelectTrigger className="mt-2" data-testid="equipe-supervisor-select"><SelectValue placeholder="Selecione o supervisor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nenhum">Nenhum</SelectItem>
                  {supervisores.map(sup => (
                    <SelectItem key={sup.id} value={sup.id}>{sup.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {supervisores.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">Nenhum supervisor cadastrado. Crie um usuario com perfil SUPERVISOR primeiro.</p>
              )}
            </div>

            {/* Membros (Vendedores) */}
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <Label className="text-purple-800 font-semibold flex items-center gap-2 mb-3">
                <Users className="h-4 w-4" />Vendedores da Equipe
              </Label>
              <p className="text-xs text-purple-600 mb-3">Selecione os vendedores que fazem parte desta equipe:</p>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {vendedores.map(v => (
                  <div key={v.id} className="flex items-center space-x-2 p-2 bg-white rounded border">
                    <Checkbox
                      id={`membro-${v.id}`}
                      checked={equipeForm.membros_ids.includes(v.id)}
                      onCheckedChange={(checked) => {
                        setEquipeForm({
                          ...equipeForm,
                          membros_ids: checked
                            ? [...equipeForm.membros_ids, v.id]
                            : equipeForm.membros_ids.filter(id => id !== v.id)
                        });
                      }}
                    />
                    <label htmlFor={`membro-${v.id}`} className="text-sm font-medium cursor-pointer flex-1">
                      {v.nome}
                    </label>
                  </div>
                ))}
              </div>
              {vendedores.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">Nenhum vendedor cadastrado.</p>
              )}
              <p className="text-xs text-purple-500 mt-2">
                Selecionados: {equipeForm.membros_ids.length} vendedor(es)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEquipeDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleSalvarEquipe} data-testid="btn-salvar-equipe">
              {editandoEquipe ? 'Atualizar' : 'Criar'} Equipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Usuarios;
